import React from 'react';

/**
 * Wraps the given phrases in bold ink wherever they appear in a string,
 * leaving the rest of the sentence alone.
 *
 * Used only in the legal documents, where someone is scanning a wall of
 * prose for the few facts that actually affect them: how long something is
 * kept, what the lawful basis is, what right they hold, what we have
 * promised not to do.
 *
 * Three rules keep it from turning into decoration:
 * - it is `<strong>`, so the emphasis reaches a screen reader as well as
 *   the eye. Weight and colour are never the only carrier,
 * - it uses `text-ink` on the body's lighter grey rather than an accent
 *   colour, because this is body text at body size and has to clear 4.5:1
 *   without relying on hue to mean anything, and
 * - the phrase must appear verbatim in the source string, so the copy
 *   stays editable in one place and a highlight can never silently
 *   rewrite it.
 *
 * Phrases are matched longest first, so an overlapping shorter phrase
 * cannot cut a longer one in half.
 */
export function highlight(text: string, phrases?: readonly string[]): React.ReactNode {
  if (!phrases || phrases.length === 0) return text;
  const found = [...phrases].filter(phrase => text.includes(phrase)).sort((a, b) => b.length - a.length);
  if (found.length === 0) return text;
  const escaped = found.map(phrase => phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'g'));
  return parts.map((part, index) => found.includes(part) ? <strong key={`${part}-${index}`} className="font-bold text-ink" data-id="element-905" data-mp="1d8k9ks6gccvv">
        {part}
      </strong> : part);
}